import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Terminal } from 'lucide-react';
import type { OnuConfigPlan } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { Card, Spinner, TBody, THead, TRow, Table } from '../../components/ui';

/** The slices of the OMCI read this card actually renders. */
interface OmciSlice {
  interfaceName?: string;
  /** ZTE rows carry userVlan+vport; Huawei rows carry vlanId+gemIndex. */
  servicePorts?: Array<{
    index: number;
    vport?: number | null;
    userVlan?: number;
    vlan?: number;
    vlanId?: number;
    gemIndex?: number | null;
    rxTable?: number | null;
    txTable?: number | null;
  }>;
  running?: {
    flows: Array<{ flow: number; priority: number; vlanId: number }>;
    ports: Array<{ port: string; mode: string; pvid: number | null; taggedVlans: number[] }>;
    ipHostVlans: Array<{ hostId: number; priority: number; vlanId: number }>;
    veipBinds: number[];
  } | null;
  tr069?: { vlanId: number | null } | null;
  cached?: boolean;
  fetchedAt?: number;
}

/**
 * What the OLT actually carries for this ONU — data, not sentences.
 *
 * This card used to say "15 commands for the OLT" and stop, which answers
 * nothing an operator diagnosing a subscriber is asking. The question is
 * "which VLANs has the chassis assigned, and where do they go?" — so the card
 * now reads the same OMCI snapshot the Settings cards use and lays the answer
 * out per VLAN: the service-port carrying it across the PON, the flow it
 * rides, and where it comes out (a socket, the ONU's own ip-host, or the
 * TR-069 management tag).
 *
 * Blockers stay — they are the things that stop a write landing at all — and
 * the composed commands stay reachable behind a details fold for whoever is
 * diagnosing at that depth.
 */
export function ConfigPlanCard({
  base,
  oltId,
  onuId,
}: {
  base: string;
  oltId?: string;
  onuId?: string;
}) {
  const planQuery = useQuery({
    queryKey: ['onu-config', base, 'config-plan'],
    queryFn: () => api.get<OnuConfigPlan>(`${base}/config-plan`),
  });
  const omciQuery = useQuery({
    // Same key the Settings cards use, so one read serves both.
    queryKey: ['onu', onuId, 'omci-config'],
    queryFn: () =>
      api.get<OmciSlice>(
        `/olts/${encodeURIComponent(oltId!)}/onus/${encodeURIComponent(onuId!)}/omci-config`,
      ),
    enabled: Boolean(oltId && onuId),
  });

  if (planQuery.isLoading) {
    return (
      <Card title="On the OLT">
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      </Card>
    );
  }

  const plan = planQuery.data;
  if (!plan) return null;

  const config = omciQuery.data;
  const flows = config?.running?.flows ?? [];
  const ports = config?.running?.ports ?? [];
  const ipHosts = config?.running?.ipHostVlans ?? [];
  const servicePorts = config?.servicePorts ?? [];

  /** Where one VLAN leaves the ONU, said in the chassis's own objects. */
  const reaches = (vlan: number): string => {
    const out: string[] = [];
    for (const p of ports) {
      if (p.pvid === vlan) out.push(`${p.port} untagged`);
      else if (p.taggedVlans.includes(vlan)) out.push(`${p.port} tagged`);
    }
    for (const h of ipHosts.filter((h) => h.vlanId === vlan)) {
      out.push(
        config?.tr069?.vlanId === vlan ? `ip-host ${h.hostId} · management` : `ip-host ${h.hostId} · ONU-routed`,
      );
    }
    if (out.length === 0 && config?.tr069?.vlanId === vlan) out.push('TR-069 management tag');
    return out.join(', ') || '—';
  };

  const rows = servicePorts.map((sp) => {
    const vlan = sp.userVlan ?? sp.vlanId ?? sp.vlan ?? 0;
    const flow = flows.find((f) => f.vlanId === vlan);
    return { sp, vlan, flow };
  });

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Terminal className="size-4 text-muted" />
          On the OLT
          {plan.oltName && <span className="text-xs font-normal text-faint">{plan.oltName}</span>}
        </span>
      }
    >
      <div className="space-y-4 p-5">
        {plan.blockers.map((blocker) => (
          <div
            key={blocker}
            className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft px-4 py-2.5 text-sm text-danger"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            {blocker}
          </div>
        ))}

        {omciQuery.isLoading && oltId ? (
          <div className="flex justify-center p-4">
            <Spinner />
          </div>
        ) : rows.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <THead>
                <tr>
                  <th>VLAN</th>
                  <th>Service-port</th>
                  <th>Flow</th>
                  <th>Comes out at</th>
                </tr>
              </THead>
              <TBody>
                {rows.map(({ sp, vlan, flow }) => (
                  <TRow key={`${sp.index}-${vlan}`}>
                    <td className="font-mono text-ink">{vlan}</td>
                    <td className="font-mono">
                      {sp.index}
                      {sp.vport != null ? ` · vport ${sp.vport}` : ''}
                      {sp.gemIndex != null ? ` · GEM ${sp.gemIndex}` : ''}
                      {sp.rxTable != null || sp.txTable != null
                        ? ` · tables ${sp.rxTable ?? '—'}/${sp.txTable ?? '—'}`
                        : ''}
                    </td>
                    <td className="font-mono">
                      {flow ? `flow ${flow.flow} · pri ${flow.priority}` : '—'}
                    </td>
                    <td>{reaches(vlan)}</td>
                  </TRow>
                ))}
              </TBody>
            </Table>
          </div>
        ) : config ? (
          <p className="text-sm text-muted">
            The chassis reports no service-ports for this ONU — nothing is assigned to it there
            yet.
          </p>
        ) : (
          <p className="text-sm text-muted">
            {plan.commands.length > 0
              ? `${plan.commands.length} command${plan.commands.length === 1 ? '' : 's'} recorded for the OLT.`
              : 'Nothing configured yet — attach a VLAN or set up a port.'}
          </p>
        )}

      </div>
    </Card>
  );
}
